import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Model,
  ModalHeader,
  ModalBody,
  Spinner
} from 'components/atoms';
import { getAdminEmployeeInfo } from 'store/Admin';
import { ApiResponseStatus } from 'models/enums';
import { AdminEmployeeInfo } from 'models/models';
import './index.scss';

interface Supervisor {
  id: string;
  name: string;
  department: string;
  email: string;
  avatar?: string;
}

interface SupervisorSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (supervisors: Supervisor[]) => void;
  selectedSupervisors?: Supervisor[];
}

export const SupervisorSelectModal = ({
  isOpen,
  onClose,
  onSelect,
  selectedSupervisors = []
}: SupervisorSelectModalProps) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [localSelectedSupervisors, setLocalSelectedSupervisors] =
    useState<Supervisor[]>(selectedSupervisors);

  const { employeeData, getUsersStatus } = useSelector(
    (state: any) => state.admin
  );

  useEffect(() => {
    if (isOpen && employeeData.length === 0) {
      dispatch(getAdminEmployeeInfo() as any);
    }
    if (isOpen) {
      setLocalSelectedSupervisors([...selectedSupervisors]);
    }
  }, [isOpen, dispatch, employeeData.length, selectedSupervisors]);

  // Transform employee data to supervisor format
  const supervisors: Supervisor[] = employeeData
    .filter(
      (employee: AdminEmployeeInfo) =>
        employee.isActive === true || employee.isActive === 'true'
    )
    .map((employee: AdminEmployeeInfo) => ({
      id: employee.userRoleId,
      name: employee.userName,
      department: employee.designationName || 'N/A',
      email: employee.upn,
      avatar: employee.employeeNameWithInitials
        ? employee.employeeNameWithInitials
            .split(' ')
            .map((name) => name.charAt(0))
            .join('')
            .toUpperCase()
        : employee.userName
            .split(' ')
            .map((name) => name.charAt(0))
            .join('')
            .slice(0, 2)
            .toUpperCase()
    }));

  const filteredSupervisors = supervisors.filter(
    (supervisor) =>
      supervisor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supervisor.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supervisor.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleToggleSupervisor = (supervisor: Supervisor) => {
    const isSelected = localSelectedSupervisors.some(
      (s) => s.id === supervisor.id
    );
    if (isSelected) {
      setLocalSelectedSupervisors((prev) =>
        prev.filter((s) => s.id !== supervisor.id)
      );
    } else {
      setLocalSelectedSupervisors((prev) => [...prev, supervisor]);
    }
  };

  const handleConfirmSelection = () => {
    onSelect(localSelectedSupervisors);
    onClose();
  };

  const handleCancelSelection = () => {
    setLocalSelectedSupervisors([...selectedSupervisors]);
    onClose();
  };

  const isSupervisorSelected = (supervisorId: string) => {
    return localSelectedSupervisors.some((s) => s.id === supervisorId);
  };

  return (
    <Model modalSize="md" showModal={isOpen} onClose={onClose}>
      <ModalHeader headerTitle="Select Supervisors" onClose={onClose} />
      <ModalBody>
        <div className="supervisor-select-modal">
          {/* Action buttons moved to top */}
          <div className="modal-actions-header">
            <div className="actions-info">
              {localSelectedSupervisors.length > 0 && (
                <span className="selection-count-header">
                  {localSelectedSupervisors.length} supervisor(s) selected
                </span>
              )}
            </div>
            <div className="action-buttons">
              <button
                className="btn-cancel-header"
                onClick={handleCancelSelection}
                type="button"
              >
                Cancel
              </button>
              <button
                className="btn-confirm-header"
                onClick={handleConfirmSelection}
                disabled={localSelectedSupervisors.length === 0}
                type="button"
              >
                Confirm ({localSelectedSupervisors.length})
              </button>
            </div>
          </div>

          <div className="search-section">
            <label className="input-label">Search Supervisors</label>
            <input
              type="text"
              className="input-text-box"
              placeholder="Search by name, designation, or email"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          {getUsersStatus === ApiResponseStatus.pending ? (
            <div className="loading-section">
              <Spinner showSpinner={true} />
              <p>Loading employees...</p>
            </div>
          ) : (
            <div className="supervisors-list">
              {filteredSupervisors.length === 0 ? (
                <div className="no-supervisors">
                  {searchTerm
                    ? 'No employees found matching your search.'
                    : 'No employees available.'}
                </div>
              ) : (
                filteredSupervisors.map((supervisor) => (
                  <div
                    key={supervisor.id}
                    className={`supervisor-item ${
                      isSupervisorSelected(supervisor.id) ? 'selected' : ''
                    }`}
                    onClick={() => handleToggleSupervisor(supervisor)}
                  >
                    <div className="supervisor-avatar">{supervisor.avatar}</div>
                    <div className="supervisor-info">
                      <h6>{supervisor.name}</h6>
                      <p>{supervisor.department}</p>
                      <p>{supervisor.email}</p>
                    </div>
                    <div className="selection-indicator">
                      {isSupervisorSelected(supervisor.id) ? (
                        <i className="ri-checkbox-circle-fill" />
                      ) : (
                        <i className="ri-checkbox-blank-circle-line" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </ModalBody>
    </Model>
  );
};
